import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';

const EXPERIENCE_LEVELS = ['Beginner', 'Intermediate', 'Advanced', 'Elite'];
const PACE_RANGES = ['Under 7:00', '7:00–8:00', '8:00–9:00', '9:00–10:00', '10:00–12:00', '12:00+'];
const MILEAGE_RANGES = ['Under 10', '10–20', '20–30', '30–40', '40+'];
const GOAL_OPTIONS = ['Race Training', 'Stay Fit', 'Social', 'Explore'];

type ProfileForm = {
  display_name: string;
  username: string;
  bio: string;
  location: string;
  instagram_handle: string;
  strava_link: string;
  profile_image: string;
  // Running depth fields (RC-13)
  experience_level: string;
  pace_range: string;
  weekly_mileage: string;
  neighborhood: string;
  goals: string[];
};

function ChipSelect({
  options,
  value,
  onChange,
  label,
}: {
  options: string[];
  value: string;
  onChange: (v: string) => void;
  label: string;
}) {
  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-black">{label}</label>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(value === opt ? '' : opt)}
            className={`rounded-full border px-3 py-1.5 text-sm font-medium transition ${
              value === opt
                ? 'border-black bg-black text-white'
                : 'border-black/10 bg-white text-black/60 hover:border-black hover:text-black'
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

function ChipMulti({
  options,
  value,
  onChange,
  label,
}: {
  options: string[];
  value: string[];
  onChange: (v: string[]) => void;
  label: string;
}) {
  function toggle(opt: string) {
    onChange(value.includes(opt) ? value.filter((v) => v !== opt) : [...value, opt]);
  }
  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-black">{label}</label>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => toggle(opt)}
            className={`rounded-full border px-3 py-1.5 text-sm font-medium transition ${
              value.includes(opt)
                ? 'border-black bg-black text-white'
                : 'border-black/10 bg-white text-black/60 hover:border-black hover:text-black'
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function ProfilePage() {
  const {
    user,
    profile,
    refreshProfile,
    loading: authLoading,
    signOut,
    setPassword,
  } = useAuth();

  const navigate = useNavigate();

  const [form, setForm] = useState<ProfileForm>({
    display_name: '',
    username: '',
    bio: '',
    location: '',
    instagram_handle: '',
    strava_link: '',
    profile_image: '',
    experience_level: '',
    pace_range: '',
    weekly_mileage: '',
    neighborhood: '',
    goals: [],
  });

  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [password, setPasswordInput] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const [isSigningOut, setIsSigningOut] = useState(false);

  useEffect(() => {
    const metadataAvatar =
      typeof user?.user_metadata?.avatar_url === 'string'
        ? user.user_metadata.avatar_url
        : '';

    setForm({
      display_name: profile?.display_name || '',
      username: profile?.username || '',
      bio: profile?.bio || '',
      location: profile?.location || '',
      instagram_handle: profile?.instagram_handle || '',
      strava_link: profile?.strava_link || '',
      profile_image: profile?.profile_image || metadataAvatar || '',
      experience_level: profile?.experience_level || '',
      pace_range: profile?.pace_range || '',
      weekly_mileage: profile?.weekly_mileage || '',
      neighborhood: profile?.neighborhood || '',
      goals: profile?.goals || [],
    });
  }, [profile, user]);

  const firstName = useMemo(() => {
    if (form.display_name?.trim()) return form.display_name.trim().split(' ')[0];
    if (user?.email) return user.email.split('@')[0];
    return 'Runner';
  }, [form.display_name, user]);

  // Profile completeness (RC-13 AC5)
  const completenessFields = [
    !!form.display_name.trim(),
    !!form.bio.trim(),
    !!form.profile_image,
    !!form.experience_level,
    !!form.pace_range,
    !!form.weekly_mileage,
    form.goals.length > 0,
  ];
  const completedCount = completenessFields.filter(Boolean).length;
  const totalFields = completenessFields.length;
  const completenessPercent = Math.round((completedCount / totalFields) * 100);
  const isProfileComplete = completedCount === totalFields;

  function updateField<K extends keyof ProfileForm>(key: K, value: ProfileForm[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSave(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!user) return;

    setSaving(true);
    setMessage(null);
    setErrorMessage(null);

    const payload = {
      id: user.id,
      display_name: form.display_name.trim() || null,
      username: form.username.trim() || null,
      bio: form.bio.trim() || null,
      location: form.location.trim() || null,
      instagram_handle: form.instagram_handle.trim() || null,
      strava_link: form.strava_link.trim() || null,
      profile_image: form.profile_image.trim() || null,
      experience_level: form.experience_level || null,
      pace_range: form.pace_range || null,
      weekly_mileage: form.weekly_mileage || null,
      neighborhood: form.neighborhood.trim() || null,
      goals: form.goals.length > 0 ? form.goals : null,
    };

    const { error } = await supabase.from('profiles').upsert(payload);

    if (error) {
      setErrorMessage(error.message);
    } else {
      setMessage('Profile saved.');
      await refreshProfile();
    }

    setSaving(false);
  }

  async function handleImageUpload(event: React.ChangeEvent<HTMLInputElement>) {
    if (!user) return;

    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setMessage(null);
    setErrorMessage(null);

    const fileExt = file.name.split('.').pop() || 'jpg';
    const filePath = `profiles/${user.id}/avatar-${Date.now()}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from('club-assets')
      .upload(filePath, file, { upsert: true });

    if (uploadError) {
      setErrorMessage(uploadError.message);
      setUploading(false);
      return;
    }

    const { data } = supabase.storage.from('club-assets').getPublicUrl(filePath);
    updateField('profile_image', data.publicUrl);
    setUploading(false);
    setMessage('New profile image uploaded. Save profile to replace your current image.');
  }

  async function handlePasswordSave(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPasswordMessage(null);
    setPasswordError(null);

    if (password.length < 8) {
      setPasswordError('Password must be at least 8 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }

    try {
      setSavingPassword(true);
      await setPassword(password);
      setPasswordMessage('Password updated successfully.');
      setPasswordInput('');
      setConfirmPassword('');
    } catch (error) {
      setPasswordError(error instanceof Error ? error.message : 'Unable to update password.');
    } finally {
      setSavingPassword(false);
    }
  }

  async function handleSignOut() {
    try {
      setIsSigningOut(true);
      await signOut();
      navigate('/');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Unable to sign out.');
    } finally {
      setIsSigningOut(false);
    }
  }

  if (authLoading) {
    return (
      <div className="flex min-h-[calc(100vh-80px)] items-center justify-center bg-[#f8f8f8] px-6">
        <div className="rounded-3xl border border-black/10 bg-white px-6 py-4 text-sm text-gray-500 shadow-sm">
          Loading profile…
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return (
    <div className="min-h-screen bg-[#f8f8f8] px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-8">

        {/* Header card */}
        <div className="overflow-hidden rounded-[32px] border border-black/10 bg-white shadow-sm">
          <div className="p-6 sm:p-8">
            <div className="rounded-[28px] border border-black/10 bg-gradient-to-r from-[#fff7cc] via-white to-[#f6e8fb] p-6 sm:p-8">
              <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
                <div className="flex items-center gap-4 sm:gap-5">
                  <div className="h-24 w-24 overflow-hidden rounded-[28px] border-4 border-white bg-white shadow-md">
                    {form.profile_image ? (
                      <img
                        src={form.profile_image}
                        alt={form.display_name || 'Profile'}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-black text-2xl font-semibold text-white">
                        {firstName.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>

                  <div>
                    <p className="text-xs uppercase tracking-[0.24em] text-gray-500">
                      Runner profile
                    </p>
                    <h1 className="mt-2 text-3xl font-semibold tracking-tight text-black">
                      {form.display_name || firstName}
                    </h1>
                    <p className="mt-1 text-sm text-gray-500">{user.email}</p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleSignOut}
                  disabled={isSigningOut}
                  className="inline-flex h-11 items-center justify-center rounded-full border border-black/10 bg-white px-5 text-sm font-medium text-black transition hover:border-black hover:bg-black hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSigningOut ? 'Signing out...' : 'Sign out'}
                </button>
              </div>

              {/* Completeness indicator */}
              <div className="mt-5 pt-4 border-t border-black/8">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-xs font-semibold text-black/50">
                    Profile completeness — {completedCount}/{totalFields}
                  </p>
                  {isProfileComplete && (
                    <span className="text-xs font-semibold text-emerald-600">Complete!</span>
                  )}
                </div>
                <div className="h-1.5 w-full rounded-full bg-black/8 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      isProfileComplete ? 'bg-emerald-500' : 'bg-black'
                    }`}
                    style={{ width: `${completenessPercent}%` }}
                  />
                </div>
                {!isProfileComplete && (
                  <p className="mt-1.5 text-xs text-black/40">
                    Fill in your running details below to help clubs find the right fit for you.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-8 lg:grid-cols-[1.2fr_0.8fr]">
          <form
            onSubmit={handleSave}
            className="rounded-[32px] border border-black/10 bg-white p-6 shadow-sm sm:p-8"
          >
            {/* Basic info */}
            <div className="mb-6">
              <p className="text-sm uppercase tracking-[0.2em] text-gray-500">Basic info</p>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight text-black">
                Make your runner profile feel like you
              </h2>
            </div>

            <div className="grid gap-5">
              <div>
                <label className="mb-2 block text-sm font-medium text-black">Display name</label>
                <input
                  value={form.display_name}
                  onChange={(e) => updateField('display_name', e.target.value)}
                  className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 outline-none transition focus:border-black"
                  placeholder="Ben Townsend"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-black">Username</label>
                <input
                  value={form.username}
                  onChange={(e) => updateField('username', e.target.value)}
                  className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 outline-none transition focus:border-black"
                  placeholder="btrunsfar"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-black">Bio</label>
                <textarea
                  value={form.bio}
                  onChange={(e) => updateField('bio', e.target.value)}
                  rows={3}
                  className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 outline-none transition focus:border-black"
                  placeholder="Trail runner, community builder, coffee after the run."
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-black">Location</label>
                <input
                  value={form.location}
                  onChange={(e) => updateField('location', e.target.value)}
                  className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 outline-none transition focus:border-black"
                  placeholder="San Marcos, CA"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-black">Instagram handle</label>
                <input
                  value={form.instagram_handle}
                  onChange={(e) => updateField('instagram_handle', e.target.value)}
                  className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 outline-none transition focus:border-black"
                  placeholder="@yourhandle"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-black">Strava link</label>
                <input
                  value={form.strava_link}
                  onChange={(e) => updateField('strava_link', e.target.value)}
                  className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 outline-none transition focus:border-black"
                  placeholder="https://www.strava.com/athletes/..."
                />
              </div>

              {/* Profile image */}
              <div className="rounded-[24px] border border-black/10 bg-[#fafafa] p-4">
                <label className="mb-3 block text-sm font-medium text-black">
                  Current profile image
                </label>
                <div className="flex items-center gap-4">
                  <div className="h-20 w-20 overflow-hidden rounded-[22px] border border-black/10 bg-white shadow-sm">
                    {form.profile_image ? (
                      <img
                        src={form.profile_image}
                        alt={form.display_name || 'Current profile'}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-black text-lg font-semibold text-white">
                        {firstName.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-black">This image will be replaced</p>
                    <p className="mt-1 text-sm text-gray-500">
                      Upload a new image below, then save profile to confirm.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-black">Upload new profile image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="block w-full text-sm text-gray-600"
                />
                {uploading && (
                  <p className="mt-2 text-sm text-gray-500">Uploading…</p>
                )}
              </div>
            </div>

            {/* Running depth section */}
            <div className="mt-8 border-t border-black/8 pt-8">
              <p className="text-sm uppercase tracking-[0.2em] text-gray-500">Running profile</p>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight text-black">
                Help clubs understand your running style
              </h2>
              <p className="mt-1 text-sm text-black/45">
                Visible only to you and run leaders of clubs you follow.
              </p>

              <div className="mt-6 grid gap-6">
                <ChipSelect
                  label="Experience level"
                  options={EXPERIENCE_LEVELS}
                  value={form.experience_level}
                  onChange={(v) => updateField('experience_level', v)}
                />

                <ChipSelect
                  label="Pace (min/mile)"
                  options={PACE_RANGES}
                  value={form.pace_range}
                  onChange={(v) => updateField('pace_range', v)}
                />

                <ChipSelect
                  label="Weekly mileage"
                  options={MILEAGE_RANGES}
                  value={form.weekly_mileage}
                  onChange={(v) => updateField('weekly_mileage', v)}
                />

                <div>
                  <label className="mb-2 block text-sm font-medium text-black">
                    Home neighbourhood
                  </label>
                  <input
                    value={form.neighborhood}
                    onChange={(e) => updateField('neighborhood', e.target.value)}
                    className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 outline-none transition focus:border-black"
                    placeholder="North Park, 92104…"
                  />
                </div>

                <ChipMulti
                  label="Goals"
                  options={GOAL_OPTIONS}
                  value={form.goals}
                  onChange={(v) => updateField('goals', v)}
                />
              </div>
            </div>

            {message && <p className="mt-5 text-sm text-green-600">{message}</p>}
            {errorMessage && <p className="mt-5 text-sm text-red-600">{errorMessage}</p>}

            <div className="mt-6">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex h-11 items-center justify-center rounded-full bg-black px-5 text-sm font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? 'Saving...' : 'Save profile'}
              </button>
            </div>
          </form>

          <div className="flex flex-col gap-8">
            <form
              onSubmit={handlePasswordSave}
              className="rounded-[32px] border border-black/10 bg-white p-6 shadow-sm sm:p-8"
            >
              <p className="text-sm uppercase tracking-[0.2em] text-gray-500">Security</p>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight text-black">
                Update password
              </h2>

              <div className="mt-6 grid gap-5">
                <div>
                  <label className="mb-2 block text-sm font-medium text-black">New password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPasswordInput(e.target.value)}
                    className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 outline-none transition focus:border-black"
                    placeholder="Minimum 8 characters"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-black">
                    Confirm new password
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 outline-none transition focus:border-black"
                    placeholder="Re-enter password"
                  />
                </div>
              </div>

              {passwordMessage && (
                <p className="mt-5 text-sm text-green-600">{passwordMessage}</p>
              )}
              {passwordError && <p className="mt-5 text-sm text-red-600">{passwordError}</p>}

              <div className="mt-6">
                <button
                  type="submit"
                  disabled={savingPassword}
                  className="inline-flex h-11 items-center justify-center rounded-full bg-[#ffde59] px-5 text-sm font-semibold text-black transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {savingPassword ? 'Updating...' : 'Update password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
